/**
 * annotation-hover-store — single-slot Zustand store that tracks which
 * annotation is currently hovered, regardless of type (comment,
 * drawing, future kinds).
 *
 * The store carries three derived facts about the hovered annotation:
 *
 *   - `hoveredAnnotationId` — drives the comments-rail link feedback.
 *     A rail note keys off `hoveredAnnotationId === myId` so hovering
 *     the matching highlight rect (or another rail note) lights up
 *     just one note rather than re-rendering every note.
 *
 *   - `hoveredOwnerId` — drives the right-hand author-avatar dim.
 *     The avatars rail keys off
 *     `hoveredBlockId === myBlockId && hoveredOwnerId !== thisAvatarOwnerId`
 *     to know whether to dim.
 *
 *   - `hoveredBlockId` — scopes the dim feedback to the SAME card the
 *     hover originated on. Without it, hovering a comment on card A
 *     would dim avatars on every other card in the stream whose
 *     contributors happen to differ from card A's hovered author —
 *     visually disconnected noise. Scoping by block keeps the dim
 *     localized to the card the user is attending to.
 *
 * Hover producers (highlight rects in `CommentTextRangeView`, the
 * SVG body in `DrawingAnnotationView`, rail notes in
 * `BlockDetailCommentsRailContainer`) call `setHoveredAnnotation`
 * on enter/leave. Hover consumers (rail notes; author avatars)
 * subscribe to a derived selector so they only re-render when their
 * own slice flips.
 *
 * Single-slot is intentional: only one annotation can be "hovered"
 * at a time, and dispatching to a Set would invite consistency bugs
 * (e.g. a stuck "still hovered" state if a `mouseleave` is dropped
 * because the underlying element unmounted mid-gesture). Replacing
 * the slot is atomic.
 */
import { create } from "zustand";
export const useAnnotationHoverStore = create((set) => ({
    hoveredAnnotationId: undefined,
    hoveredOwnerId: undefined,
    hoveredBlockId: undefined,
    setHoveredAnnotation: (next) => set({
        hoveredAnnotationId: next?.id,
        hoveredOwnerId: next?.ownerId,
        hoveredBlockId: next?.blockId,
    }),
}));
